// Per-phase actions under the board: play controls, overlay toggles, the
// scoring toolkit (resume / finalize), and finished-game shortcuts.

import { useEffect, useRef, useState } from 'react';
import { computeUndoPlies, moveCap } from '../../shared/go/game.ts';
import { analysisAvailability } from '../availability.ts';
import { navigate, setQuery } from '../router.ts';
import { useStore } from '../store/useStore.ts';

export interface ActionBarProps {
  overlay: string;
}

export function ActionBar({ overlay }: ActionBarProps) {
  const detail = useStore((s) => s.detail);
  const record = useStore((s) => s.record);
  const play = useStore((s) => s.play);
  const undo = useStore((s) => s.undo);
  const resume = useStore((s) => s.resume);
  const finalize = useStore((s) => s.finalize);
  const playPending = useStore((s) => s.playPending);
  const scoreLoading = useStore((s) => s.scoreLoading);
  const health = useStore((s) => s.health);
  const confirm19 = useStore((s) => s.confirm19);
  const setConfirm19 = useStore((s) => s.setConfirm19);

  const [resignArmed, setResignArmed] = useState(false);
  const resignTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (resignTimer.current) clearTimeout(resignTimer.current);
    },
    [],
  );

  if (!detail || !record) return null;
  const status = detail.game.status;
  const size = detail.game.boardSize;
  const analysisState = analysisAvailability(health);
  const katagoReady = analysisState === 'ready';
  const undoPlies = computeUndoPlies(record);

  const toggleOverlay = (name: 'territory' | 'suggestions') => {
    setQuery({ overlay: overlay === name ? null : name });
  };

  const onResign = () => {
    if (resignArmed) {
      setResignArmed(false);
      void play('resign');
    } else {
      setResignArmed(true);
      resignTimer.current = setTimeout(() => setResignArmed(false), 3000);
    }
  };

  const onFinalize = async () => {
    const breakdown = await finalize();
    if (breakdown) setQuery({ modal: 'result' });
  };

  return (
    <div className="action-bar">
      {status === 'active' && (
        <>
          <div className="action-group">
            <button type="button" className="btn" disabled={playPending} onClick={() => void play('pass')}>
              Pass
            </button>
            <button
              type="button"
              className="btn"
              disabled={playPending || undoPlies === 0}
              title={undoPlies === 0 ? 'Nothing to undo yet' : `Undo ${undoPlies} ${undoPlies === 1 ? 'ply' : 'plies'}`}
              onClick={() => void undo()}
            >
              Undo
            </button>
            <button
              type="button"
              className={`btn ${resignArmed ? 'btn-danger' : ''}`}
              disabled={playPending}
              onClick={onResign}
            >
              {resignArmed ? 'Confirm resign?' : 'Resign'}
            </button>
          </div>
          <div className="action-group">
            <button
              type="button"
              className={`btn btn-toggle ${overlay === 'territory' ? 'is-on' : ''}`}
              aria-pressed={overlay === 'territory'}
              disabled={!katagoReady}
              title={katagoReady ? 'Territory estimate overlay' : 'KataGo is not ready yet'}
              onClick={() => toggleOverlay('territory')}
            >
              Territory
            </button>
            <button
              type="button"
              className={`btn btn-toggle ${overlay === 'suggestions' ? 'is-on' : ''}`}
              aria-pressed={overlay === 'suggestions'}
              disabled={!katagoReady}
              title={katagoReady ? 'Show suggested moves' : 'KataGo is not ready yet'}
              onClick={() => toggleOverlay('suggestions')}
            >
              Suggestions
            </button>
            {size === 19 && (
              <label className="confirm-toggle">
                <input
                  type="checkbox"
                  checked={confirm19}
                  onChange={(e) => setConfirm19(e.target.checked)}
                />
                Confirm moves
              </label>
            )}
          </div>
          {!katagoReady && (
            <p className="engine-note" role="status">
              Territory and suggestions are{' '}
              {analysisState === 'checking'
                ? 'being checked.'
                : analysisState === 'warming'
                ? 'warming up. You can keep playing while KataGo gets ready.'
                : 'unavailable because KataGo is offline.'}
            </p>
          )}
        </>
      )}

      {status === 'scoring' && (
        <>
          <p className="scoring-hint">
            Click a group to toggle it dead · click an empty point to toggle dame. Marks are
            editable — fix anything KataGo got wrong.
          </p>
          <div className="action-group">
            {/* Cap-forced scoring cannot resume (server 409s move_cap_reached). */}
            {record.moves.length < moveCap(size) && (
              <button type="button" className="btn" onClick={() => void resume()}>
                Resume play
              </button>
            )}
            <button
              type="button"
              className="btn btn-accent"
              disabled={scoreLoading}
              onClick={() => void onFinalize()}
            >
              Finalize score
            </button>
          </div>
        </>
      )}

      {status === 'finished' && (
        <div className="action-group">
          {detail.scoreBreakdown && (
            <button type="button" className="btn btn-accent" onClick={() => setQuery({ modal: 'result' })}>
              Show result
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => navigate(`/review/${detail.game.id}`)}
          >
            Review game
          </button>
          <button type="button" className="btn" onClick={() => navigate('/')}>
            New game
          </button>
        </div>
      )}
    </div>
  );
}
